// Minimal data-fetching hook with keyed invalidation, so a mutation on one
// screen can refresh every list that depends on it.
import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "./api";

type Listener = (prefix: string) => void;
const listeners = new Set<Listener>();

/** Re-runs every mounted query whose key starts with `prefix`. */
export function invalidate(prefix: string) {
  listeners.forEach((l) => l(prefix));
}

export interface QueryState<T> {
  data: T | undefined;
  error: ApiError | Error | null;
  loading: boolean;
  reload: () => void;
}

export function useQuery<T>(key: string | null, fn: (signal: AbortSignal) => Promise<T>, opts: { refreshMs?: number } = {}): QueryState<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const [loading, setLoading] = useState<boolean>(key !== null);
  const [tick, setTick] = useState(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (key === null) {
      setLoading(false);
      return;
    }
    const ac = new AbortController();
    setLoading(true);
    fnRef
      .current(ac.signal)
      .then((d) => {
        if (!ac.signal.aborted) {
          setData(d);
          setError(null);
        }
      })
      .catch((e: unknown) => {
        if (!ac.signal.aborted && (e as Error).name !== "AbortError") {
          setError(e as Error);
        }
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });
    return () => ac.abort();
  }, [key, tick]);

  useEffect(() => {
    if (key === null) return;
    const l: Listener = (prefix) => {
      if (key.startsWith(prefix)) setTick((t) => t + 1);
    };
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  }, [key]);

  useEffect(() => {
    if (!opts.refreshMs || key === null) return;
    const id = window.setInterval(() => setTick((t) => t + 1), opts.refreshMs);
    return () => window.clearInterval(id);
  }, [opts.refreshMs, key]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, error, loading, reload };
}

/** Tracks one async action's pending state and error. */
export function useAction<A extends unknown[], R>(fn: (...args: A) => Promise<R>) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const run = useCallback(
    async (...args: A): Promise<R | undefined> => {
      setPending(true);
      setError(null);
      try {
        return await fn(...args);
      } catch (e) {
        setError(e as Error);
        return undefined;
      } finally {
        setPending(false);
      }
    },
    [fn],
  );
  return { run, pending, error, setError };
}
